/**
 * Depo/Stok sekmesinde seçilen ürünlerin hedef depoda tanımlı olup olmadığını
 * kontrol eder ve gerekirse o depoda tanımlarını açar.
 *
 * NEDEN GEREKLİ: Ürün–depo ilişkisi `WarehouseStock` tablosunda satır bazlı
 * tutuluyor. Satış anında hedef depoda satır yoksa backend satırı kendisi açıp
 * miktarı eksiye düşürüyor (ürünün `CanBeNegative` alanı varsayılan true).
 * Yani ekranı kullanmak için ön tanım ZORUNLU DEĞİL; buradaki akış, ürünlerin
 * depo listelerinde/raporlarda ilk satıştan önce de görünmesi için var.
 *
 * TRANSFER DEĞİLDİR: kaynak deponun miktarlarına dokunulmaz, sadece hedef
 * depoda eksik olan ürünler için yeni satır açılır.
 */
import salesService from '../../../../services/salesService';
import { InventoryService } from '../../../accounting/inventory/services/inventoryService';

export interface MissingWarehouseProduct {
  id: string;
  name: string;
  stockCode?: string;
}

const normalizeId = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * Verilen ürünlerden hangilerinin hedef depoda stok satırı olmadığını döndürür.
 *
 * `products/by-ids` ucu her ürünle birlikte TÜM depolardaki stok satırlarını da
 * getirdiği için tek çağrı yeterli; ürün başına ayrı sorgu yapılmıyor. Bu uç,
 * ayar modalının ürün detaylarını yüklerken kullandığı uçla aynı.
 */
export async function findProductsMissingInWarehouse(
  productIds: string[],
  warehouseId: string
): Promise<MissingWarehouseProduct[]> {
  if (!warehouseId || productIds.length === 0) return [];

  const items = await salesService.getProductsByIds(productIds);
  const itemsById = new Map<string, any>();
  items.forEach((item: any) => {
    const id = (item?.id ?? item?.Id)?.toString();
    if (id) itemsById.set(normalizeId(id), item);
  });

  const wantedWarehouse = normalizeId(warehouseId);
  const missing: MissingWarehouseProduct[] = [];

  productIds.forEach((productId) => {
    const item = itemsById.get(normalizeId(productId));
    // Ürün hiç dönmediyse (silinmiş ya da başka şirkete ait) dokunmuyoruz:
    // depoya var olmayan bir ürünü tanımlamaya çalışmak hata üretir.
    if (!item) return;

    const stocks: any[] = item.warehouseStocks ?? item.WarehouseStocks ?? [];
    const exists = stocks.some(
      (stock) => normalizeId((stock?.warehouseId ?? stock?.WarehouseId)?.toString()) === wantedWarehouse
    );
    if (exists) return;

    missing.push({
      id: productId,
      name: (item.name ?? item.Name ?? '').toString() || productId,
      stockCode: (item.stockCode ?? item.StockCode ?? undefined)?.toString() || undefined,
    });
  });

  return missing;
}

/**
 * Ürünleri hedef depoda 1 adetle tanımlar. Uç, satırı zaten olan ürünleri
 * atladığı için mevcut miktarlar korunur.
 */
export async function createProductsInWarehouse(
  companyId: string,
  warehouseId: string,
  productIds: string[]
): Promise<number> {
  if (!companyId || !warehouseId || productIds.length === 0) return 0;

  const result = await InventoryService.bulkAssignWarehouse(companyId, productIds, warehouseId, 1);
  return result?.count ?? productIds.length;
}
